import { logger } from "@/lib/logger";

/**
 * Fire-and-forget Hub→Slack notification POSTs.
 *
 * These must never block or break task/time-entry creation, so failures are
 * non-fatal. They are, however, LOGGED: `fetch` resolves normally on a 4xx/5xx,
 * so silently discarding the response hid a 400 "Unknown payload type" on every
 * single task for months (#hub-tasks-notifications received zero posts). A
 * notification nobody is told broke isn't a notification.
 */
async function postNotification(payload: unknown, kind: string): Promise<void> {
  try {
    const res = await fetch("/api/webhook/slack", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      logger.warn("slack notification rejected", {
        op: "slackWebhook.clientPost",
        kind,
        status: res.status,
      });
    }
  } catch (error) {
    // Non-fatal: never block UX on a notification.
    logger.warn("slack notification failed to send", {
      op: "slackWebhook.clientPost",
      kind,
      error,
    });
  }
}

/**
 * `notifyTaskCreated` was removed deliberately — do not reintroduce it.
 *
 * Task cards are posted by the `contact_tasks_created` DATABASE webhook
 * (`app/api/webhooks/tasks/created/route.ts`), so every writer notifies: the UI,
 * Meridian MCP `create_task`, the Slack sales modal, quote-request automation,
 * recurring tasks, `POST /api/webhooks/tasks`, and raw SQL. Calling a
 * client-side notifier again from a component would double-post the card, and
 * would re-create the original defect where only UI-created tasks were ever
 * announced.
 *
 * Time entries still post from the client because there is no database webhook
 * for `time_entries` yet. If one is added, retire this file entirely.
 */
export async function notifyTimeEntryCreated(timeEntry: unknown) {
  await postNotification(timeEntry, "time entry");
}
